import { Product } from '@components/ui/Product';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { faChevronDown, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { pickingOrdersQueryKey, usePickingFlow, useProductSorting } from '../../hooks';
import { usePreparationQuery, preparationQueryKey } from '../../hooks/usePreparationQuery';
import { usePickingRouteMap } from '../../hooks/usePickingRouteMap';
import { GridCanvas } from '@modules/WarehouseMap/components/display/GridCanvas';
import { List, Map as MapIcon } from 'lucide-react';
import {
  ActiveLocationSection,
  Breadcrumb,
  CompletedMessage,
  CompletedProductsButton,
  CompletedProductsModal,
  LinePickCompletedCard,
  PreparationStatusMessage,
  ProductSortControls,
} from '../display';
import { FloatingButtons, QuantityConfirm, ScannerInput } from '../interaction';
import { AdjustDiscrepancyModal, CancelPickingModal } from '../modals';
import type { AdjustDiscrepancyModalRef } from '../modals';
import type { AdjustPickingDiscrepancyResponse } from '../../models';
import type { LinePick, Location, Preparation } from '../../models/PreparationModels';
import type { PickingStep } from '../../hooks/usePickingFlow';
import type { PreparationLineWithProduct } from '@modules/OrderPreparation/Picking';
import type { SortConfig } from '../display/ProductSortControls';

const PREPARATION_TYPE_PICKING = 3;

const STATUS_CANCELED = 0;
const STATUS_AVAILABLE = 1;
const STATUS_COMPLETED = 2;
const STATUS_IN_PROGRESS = 3;

const Loading = ({ message }: { message: string }) => (
  <div className="container mx-auto p-4 max-w-6xl">
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">{message}</p>
      </div>
    </div>
  </div>
);

const ErrorMessage = ({ label, detail }: { label: string; detail: string }) => (
  <div className="container mx-auto p-4 max-w-6xl">
    <div className="text-center py-8">
      <p className="text-red-600 dark:text-red-400">
        {label}: {detail}
      </p>
    </div>
  </div>
);

const NotFound = () => (
  <div className="container mx-auto p-4 max-w-6xl">
    <div className="text-center py-8">
      <p className="text-muted-foreground">No se encontró la preparación</p>
    </div>
  </div>
);

const NotCorrectType = ({ preparation }: { preparation: Preparation }) => (
  <>
    <Breadcrumb preparation={preparation} />
    <PreparationStatusMessage
      status={preparation.status}
      type={preparation.type}
      preparationCode={preparation.code}
      reason="type"
    />
  </>
);

const VARIANT_STYLES = {
  completed: {
    qtyColor: 'text-green-600 dark:text-green-400',
    barBg: 'bg-green-200 dark:bg-green-800',
    barFill: 'bg-green-500',
  },
  pending: {
    qtyColor: 'text-foreground',
    barBg: 'bg-muted',
    barFill: '', // dynamic based on qty_done
  },
};

const ProductLineItem = ({
  line,
  variant = 'pending',
  locations,
  otherLocationsExpanded,
  onToggleOtherLocations,
}: {
  line: PreparationLineWithProduct;
  variant?: 'completed' | 'pending';
  locations?: { id: number; code: string; stock: number }[];
  otherLocationsExpanded?: boolean;
  onToggleOtherLocations?: () => void;
}) => {
  const styles = VARIANT_STYLES[variant];
  const progress = Math.min((line.qty_done / line.qty) * 100, 100);
  const otherLocations = Array.isArray(line.other_locations) ? line.other_locations : [];
  const hasOtherLocations = otherLocations.length > 0;

  const showLocationRow = (locations && locations.length > 0) || hasOtherLocations;

  return (
    <>
      {showLocationRow && (
        <div className="mb-3">
          <div className="flex flex-wrap gap-2">
            {(locations ?? []).map((location) => (
              <div
                key={location.id}
                className="flex items-center gap-1.5 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-sm font-medium border border-blue-200 dark:border-blue-800"
              >
                {location.code}
                <span className="text-xs opacity-75">Stock: {location.stock}</span>
              </div>
            ))}
            {hasOtherLocations && onToggleOtherLocations && (
              <button
                type="button"
                onClick={onToggleOtherLocations}
                className="flex items-center space-x-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-muted transition-colors"
              >
                <FontAwesomeIcon icon={otherLocationsExpanded ? faChevronDown : faChevronRight} className="w-3 h-3" />
                <span>
                  +{otherLocations.length} ubicación{otherLocations.length !== 1 ? 'es' : ''}
                </span>
              </button>
            )}
          </div>
        </div>
      )}

      {otherLocationsExpanded && hasOtherLocations && (
        <div className="mb-3 ml-4">
          <div className="text-xs text-muted-foreground mb-2">Otras ubicaciones:</div>
          <div className="flex flex-wrap gap-2">
            {otherLocations.map((location) => (
              <div
                key={location.id}
                className="bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-full text-sm border border-gray-200 dark:border-gray-700"
              >
                {location.code}
                <span className="ml-2 text-xs opacity-75">Stock: {location.stock}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex-1">
          <Product
            id={line.product_id}
            src={line.product?.image || ''}
            name={line.product?.name || `Producto ID: ${line.product_id}`}
            code={line.product?.code}
            acronym={line.product?.name?.charAt(0).toUpperCase()}
            showImageModal={true}
            enableLineClamp={true}
          />
        </div>

        <div className="flex flex-col items-end space-y-1 ml-4">
          <div className="flex items-center space-x-2">
            <span className={`text-lg font-semibold ${styles.qtyColor}`}>{line.qty_done}</span>
            <span className="text-muted-foreground">/</span>
            <span className="text-lg font-semibold text-muted-foreground">{line.qty}</span>
          </div>

          <div className={`w-16 h-2 ${styles.barBg} rounded-full overflow-hidden`}>
            <div
              className={`h-full ${variant === 'completed' ? styles.barFill : line.qty_done > 0 ? 'bg-yellow-500' : 'bg-gray-300'}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

interface PickingActionPanelProps {
  isCompleted: boolean;
  preparationId: number;
  preparationStatus: number;
  selectedLine?: PreparationLineWithProduct;
  selectedLocationCode: string;
  selectedLocation?: Location | null;
  currentStep: PickingStep;
  stepDescription: string;
  assignedCart?: string;
  onConfirm: (
    quantity: number,
    batchId?: number,
  ) =>
    | Promise<{ success: boolean; message: string; quantity?: number }>
    | { success: boolean; message: string; quantity?: number };
  onCancel: () => void;
  onScan: (code: string) => Promise<{ success: boolean; message: string }> | { success: boolean; message: string };
  disabled?: boolean;
}

const PickingActionPanel = ({
  isCompleted,
  preparationId,
  preparationStatus,
  selectedLine,
  selectedLocationCode,
  selectedLocation,
  currentStep,
  stepDescription,
  assignedCart,
  onConfirm,
  onCancel,
  onScan,
  disabled,
}: PickingActionPanelProps) => {
  if (isCompleted) {
    return <CompletedMessage preparationId={preparationId} status={preparationStatus} />;
  }

  if (selectedLine) {
    return (
      <QuantityConfirm
        selectedLine={selectedLine}
        selectedLocationCode={selectedLocationCode}
        selectedLocation={selectedLocation}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    );
  }

  return (
    <ScannerInput
      currentStep={currentStep}
      stepDescription={stepDescription}
      onScan={onScan}
      disabled={disabled}
      cartCode={assignedCart}
    />
  );
};

interface PendingProductsPanelProps {
  pendingProducts: PreparationLineWithProduct[];
  sortConfig: SortConfig;
  onSortChange: (config: SortConfig) => void;
  routeMap: {
    grid: any;
    racks: any[];
    route: any;
    selectedCells: Set<string>;
  };
}

const PendingProductsPanel = ({ pendingProducts, sortConfig, onSortChange, routeMap }: PendingProductsPanelProps) => {
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [expandedOtherLineIds, setExpandedOtherLineIds] = useState<Set<number>>(new Set());
  const hasMapData = !!(routeMap.grid && routeMap.racks.length > 0);

  const hasPending = pendingProducts.length > 0;

  const toggleOtherLocations = useCallback((lineId: number) => {
    setExpandedOtherLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  }, []);

  return (
    <div className="flex-1 lg:pl-8 min-h-0 flex flex-col">
      {hasPending && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2 lg:mb-4 flex-shrink-0">
          <h2 className="flex text-lg font-semibold text-foreground items-center space-x-2">
            <span className="text-orange-600 dark:text-orange-400">Productos pendientes</span>
            <span className="text-sm text-muted-foreground">({pendingProducts.length})</span>
          </h2>
          <div className="flex items-center gap-2 sm:justify-end">
            {viewMode === 'list' && <ProductSortControls sortConfig={sortConfig} onSortChange={onSortChange} />}
            {hasMapData && (
              <button
                onClick={() => setViewMode(viewMode === 'list' ? 'map' : 'list')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-border hover:bg-muted/50 transition-colors"
              >
                {viewMode === 'list' ? <MapIcon className="w-4 h-4" /> : <List className="w-4 h-4" />}
                <span className="hidden xl:inline">{viewMode === 'list' ? 'Mapa' : 'Lista'}</span>
              </button>
            )}
          </div>
        </div>
      )}

      {hasPending && viewMode === 'map' && hasMapData ? (
        <div className="flex-1 min-h-0 rounded-lg overflow-hidden border border-border">
          <GridCanvas
            grid={routeMap.grid!}
            racks={routeMap.racks}
            route={routeMap.route}
            selectedCells={routeMap.selectedCells}
            theme="dark"
            interactive={false}
          />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4">
            {pendingProducts.length === 0 ? (
              <div className="bg-muted/30 border-b border-border/50 p-6">
                <div className="flex items-center space-x-2 mb-3 text-orange-600 dark:text-orange-400">
                  <span className="text-lg font-semibold">¡Todos los productos han sido completados!</span>
                </div>
              </div>
            ) : (
              pendingProducts.map((line) => (
                <div key={line.id} className="rounded-lg border border-border p-4 hover:bg-muted/50 bg-background">
                  <ProductLineItem
                    line={line}
                    variant="pending"
                    locations={Array.isArray(line.suggested_locations) ? line.suggested_locations : []}
                    otherLocationsExpanded={expandedOtherLineIds.has(line.id)}
                    onToggleOtherLocations={() => toggleOtherLocations(line.id)}
                  />
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CompletedProductsMobile = ({
  linePicks,
  lines,
}: {
  linePicks: LinePick[];
  lines: PreparationLineWithProduct[];
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <CompletedProductsButton completedCount={linePicks.length} onClick={() => setIsOpen(true)} />
      <CompletedProductsModal isOpen={isOpen} onClose={() => setIsOpen(false)} linePicks={linePicks} lines={lines} />
    </>
  );
};

export const Main: React.FC = () => {
  const { t } = useTranslation('pickingModule');
  const { id } = useParams<{ id: string }>();
  const preparationId = id ? parseInt(id, 10) : 0;

  const queryClient = useQueryClient();
  const { preparation, loading, error, updateLineQuantity, updatePreparation } = usePreparationQuery(preparationId);

  const {
    pickingState,
    processScannedCode,
    confirmPicking,
    resetToLocationStep,
    getStepDescription,
    getAvailableProductsInLocation,
  } = usePickingFlow(preparation, updateLineQuantity, updatePreparation);

  // Warehouse map with picking route
  const routeMap = usePickingRouteMap(preparation);

  // Hook para ordenación de productos (con ruta óptima como default)
  const { sortConfig, sortedProducts, handleSortChange } = useProductSorting(
    preparation?.lines || [],
    routeMap.route?.stops,
  );

  const sortedLinePicks = useMemo(() => {
    const picks = preparation?.line_picks;
    if (!picks?.length) return [];
    return [...picks].sort((a, b) => a.id - b.id);
  }, [preparation?.line_picks]);

  // Ref para modales
  const cancelModalRef = useRef<React.ComponentRef<typeof CancelPickingModal>>(null);
  const adjustDiscrepancyModalRef = useRef<AdjustDiscrepancyModalRef>(null);

  const handleCancelPicking = () => {
    if (preparation && cancelModalRef.current) {
      cancelModalRef.current.openWithPreparation(preparation);
    }
  };

  const handleAdjustDiscrepancy = () => {
    if (!preparation || !adjustDiscrepancyModalRef.current) return;
    const initialDiscrepancy = pickingState.selectedLine
      ? {
          product_id: pickingState.selectedLine.product_id,
          ...(pickingState.selectedLocation?.id ? { location_id: pickingState.selectedLocation.id } : {}),
          qty: 1,
        }
      : undefined;
    adjustDiscrepancyModalRef.current.openWithContext(preparation, initialDiscrepancy);
  };

  const handleDiscrepancyAdjusted = (response: AdjustPickingDiscrepancyResponse) => {
    updatePreparation(response.preparation);
    queryClient.invalidateQueries({ queryKey: preparationQueryKey(preparationId) });
    queryClient.invalidateQueries({ queryKey: pickingOrdersQueryKey(preparationId) });
  };

  const handlePickingCancelled = (updatedPreparation: Preparation) => {
    if (updatePreparation) {
      updatePreparation(updatedPreparation);
    }
  };

  if (!id) {
    return <ErrorMessage label={t('error')} detail="No se proporcionó un ID de preparación" />;
  }

  if (loading) {
    return <Loading message={t('loading')} />;
  }

  if (error) {
    return <ErrorMessage label={t('error')} detail={error} />;
  }

  if (!preparation) {
    return <NotFound />;
  }

  // Validar tipo de preparación (solo tipo 3 puede hacer picking)
  const isCorrectType = preparation.type === PREPARATION_TYPE_PICKING;

  const isAvailableForPicking = preparation.status === STATUS_AVAILABLE || preparation.status === STATUS_IN_PROGRESS;
  const isCompleted = preparation.status === STATUS_COMPLETED;
  const isCancelled = preparation.status === STATUS_CANCELED;

  if (isCancelled || (!isAvailableForPicking && !isCompleted)) {
    return (
      <>
        <Breadcrumb preparation={preparation} />
        <PreparationStatusMessage
          status={preparation.status}
          type={preparation.type}
          preparationCode={preparation.code}
          reason="status"
        />
      </>
    );
  }

  // Mostrar mensaje de error si el tipo no es correcto
  if (!isCorrectType) {
    return <NotCorrectType preparation={preparation} />;
  }

  const handleGoBackToLocation = () => {
    resetToLocationStep();
  };

  return (
    <>
      <div className="container mx-auto p-4 max-w-6xl h-[calc(100vh-6rem)] flex flex-col">
        <div className="mb-6">
          <Breadcrumb preparation={preparation} />
        </div>

        <FloatingButtons
          preparation={preparation}
          onCancelPicking={handleCancelPicking}
          onAdjustDiscrepancy={handleAdjustDiscrepancy}
        />

        <div
          className={`flex flex-col lg:flex-row flex-1 min-h-0 ${sortedLinePicks.length > 0 ? 'pb-20 lg:pb-0' : ''}`}
        >
          {/* Left panel: scanner + active location + completed (desktop only) */}
          <div className="lg:flex-1 lg:pr-8 flex-shrink-0 lg:flex lg:flex-col">
            <div className="flex-shrink-0 mb-4">
              <PickingActionPanel
                isCompleted={isCompleted}
                preparationId={preparation.id}
                preparationStatus={preparation.status}
                selectedLine={pickingState.selectedLine}
                selectedLocationCode={pickingState.selectedLocation?.code || ''}
                selectedLocation={pickingState.selectedLocation}
                currentStep={pickingState.currentStep}
                stepDescription={getStepDescription()}
                assignedCart={pickingState.assignedCart}
                onConfirm={confirmPicking}
                onCancel={handleGoBackToLocation}
                onScan={processScannedCode}
                disabled={pickingState.assignedCart?.startsWith('LOADING-')}
              />
            </div>

            {isAvailableForPicking && pickingState.selectedLocation && !pickingState.selectedLine && (
              <div className="flex-shrink-0 mb-4">
                <ActiveLocationSection
                  location={pickingState.selectedLocation}
                  products={getAvailableProductsInLocation()}
                  onGoBack={handleGoBackToLocation}
                />
              </div>
            )}

            {/* Picks completados (line_picks de la API) - desktop; móvil: modal inferior */}
            {sortedLinePicks.length > 0 && (
              <div className="hidden lg:flex flex-1 mt-6 min-h-0 flex-col">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex-shrink-0 flex items-center space-x-2">
                  <span className="text-green-600 dark:text-green-400">Productos completados</span>
                  <span className="text-sm text-muted-foreground">({sortedLinePicks.length})</span>
                </h2>

                <div className="flex-1 overflow-y-auto min-h-0">
                  <div className="space-y-4 pr-1">
                    {sortedLinePicks.map((pick) => {
                      const pline = preparation.lines.find((l) => l.id === pick.stock_location_order_line_id);
                      return (
                        <div
                          key={pick.id}
                          className="border-b pb-4 bg-green-50/30 dark:bg-green-950/30 border-green-200 dark:border-green-800"
                        >
                          <LinePickCompletedCard pick={pick} line={pline} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Vertical separator - desktop only */}
          <div className="hidden lg:block w-px bg-border flex-shrink-0" />

          <PendingProductsPanel
            pendingProducts={sortedProducts.pending}
            sortConfig={sortConfig}
            onSortChange={handleSortChange}
            routeMap={routeMap}
          />
        </div>
      </div>

      <CompletedProductsMobile linePicks={sortedLinePicks} lines={preparation.lines} />

      <CancelPickingModal ref={cancelModalRef} onCancel={handlePickingCancelled} />
      <AdjustDiscrepancyModal ref={adjustDiscrepancyModalRef} onSuccess={handleDiscrepancyAdjusted} />
    </>
  );
};

export default Main;
